import React from 'react'
import { TicketStatus } from '../TicketStatusCard/TicketStatusBadge.types'
import TicketInfoCard from './TicketInfoCard'
import BetDetailCard from './BetDetailCard'
import BetDetailsTable from './BetDetailsTable'
import type { TicketViewMode } from './ViewToggle.types'
import type {
  SetLotteryMatchType,
  RawBetType,
} from '../../types/setLottery.types'

/** A group of winning numbers for a specific match pattern (used in set lottery) */
export interface WinningNumberGroup {
  /** i18n key for the group label, e.g. 'setLottery.6DigitMatch' */
  labelKey: string
  /** Fallback display label, e.g. '6-Digit Match' */
  label: string
  /** Winning numbers in this group */
  numbers: string[]
  /** Optional row index — groups sharing the same row number render side-by-side in columns */
  row?: number
}

export interface BetDetailItem {
  betType: string
  rawBetType?: RawBetType | string
  rowNumber: string | number
  status: TicketStatus
  betNumber: string
  rewardResult?: number | string
  payoutRate: number
  limitedPayoutRate?: number
  betAmount: number
  finalPayout?: number
  /** Set lottery: match type key */
  matchType?: SetLotteryMatchType | string
  /** Set lottery: maximum possible reward amount for this bet type */
  maxRewardAmount?: number
  /** Set lottery: collapsible winning number groups for the draw */
  winningNumbers?: WinningNumberGroup[]
}

export interface TicketDetailsProps {
  ticketNumber?: number | string
  betDate: string
  listTitle: string
  drawDate: string
  amount: number
  winAmount: number
  status: TicketStatus
  canCancel: boolean
  onCancel?: () => void
  onClickCheckResult?: () => void
  betDetails: BetDetailItem[]
  /** When set, only the selected layout is shown. Defaults to 'ticket'. */
  viewMode?: TicketViewMode
}

export const TicketDetails: React.FC<TicketDetailsProps> = ({
  ticketNumber,
  betDate,
  listTitle,
  drawDate,
  amount,
  winAmount,
  status,
  canCancel,
  onCancel,
  onClickCheckResult,
  betDetails,
  viewMode = 'ticket',
}) => {
  return (
    <div className="w-full max-w-5xl flex flex-col gap-4 sm:gap-6">
      {/* Ticket Info Card - always visible */}
      <TicketInfoCard
        listTitle={listTitle}
        listDate={drawDate}
        ticketNumber={ticketNumber ?? ''}
        betDate={betDate}
        amountOfBet={amount}
        winAmount={winAmount}
        status={status}
        canCancel={canCancel}
        onCancel={onCancel}
        onClickCheckResult={onClickCheckResult}
      />

      {/* Ticket layout: bet detail cards only when ticket view active */}
      {viewMode === 'ticket' && (
        <div className="flex flex-col gap-4">
          {betDetails.map((detail, index) => (
            <BetDetailCard
              key={`${detail.betNumber}-${detail.betType}-${index}`}
              betType={detail.betType}
              rawBetType={detail.rawBetType}
              rowNumber={detail.rowNumber}
              status={detail.status}
              betNumber={detail.betNumber}
              rewardResult={detail.rewardResult}
              payoutRate={detail.payoutRate}
              limitedPayoutRate={detail.limitedPayoutRate}
              betAmount={detail.betAmount}
              finalPayout={detail.finalPayout}
              matchType={detail.matchType}
              maxRewardAmount={detail.maxRewardAmount}
              winningNumbers={detail.winningNumbers}
            />
          ))}
        </div>
      )}

      {/* Table layout - only when table view active */}
      {viewMode === 'table' && <BetDetailsTable betDetails={betDetails} />}
    </div>
  )
}

export default TicketDetails
